// Generic process-wide read-through cache with LRU eviction and in-flight
// request dedup. Extracted from raytraceCache.ts / speakerSweepCache.ts /
// volumetricCache.ts, which were near-identical copies of this same
// Map + inflight-Map + bounded-LRU pattern.

export interface FetchCache<TReq, TResp> {
  /** Synchronous cache read; bumps the entry to most-recently-used. */
  getCached(req: TReq): TResp | undefined;
  /** Read-through fetch with in-flight dedup — `fn` runs only on a cold
   *  miss; concurrent callers for the same signature share one promise. */
  fetch(req: TReq, fn: (req: TReq) => Promise<TResp>): Promise<TResp>;
  /** Seed the cache without fetching — precomputed template viz lands
   *  here so the first editor paint is a hit instead of a live solve. */
  prime(req: TReq, resp: TResp): void;
  /** Drop everything — use on doc reset / sign-out. */
  clear(): void;
}

const DEFAULT_MAX_ENTRIES = 24;

export function createFetchCache<TReq, TResp>(opts: {
  sig: (req: TReq) => string;
  maxEntries?: number;
}): FetchCache<TReq, TResp> {
  const { sig, maxEntries = DEFAULT_MAX_ENTRIES } = opts;
  const cache = new Map<string, TResp>();
  const inflight = new Map<string, Promise<TResp>>();

  function getCached(req: TReq): TResp | undefined {
    const key = sig(req);
    const hit = cache.get(key);
    if (hit === undefined) return undefined;
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }

  function store(key: string, resp: TResp): void {
    cache.delete(key);
    cache.set(key, resp);
    while (cache.size > maxEntries) {
      const oldest = cache.keys().next().value;
      if (oldest === undefined) break;
      cache.delete(oldest);
    }
  }

  async function fetch(req: TReq, fn: (req: TReq) => Promise<TResp>): Promise<TResp> {
    const key = sig(req);
    const cached = getCached(req);
    if (cached !== undefined) return cached;
    const pending = inflight.get(key);
    if (pending) return pending;
    const p = (async () => {
      try {
        const resp = await fn(req);
        store(key, resp);
        return resp;
      } finally {
        inflight.delete(key);
      }
    })();
    inflight.set(key, p);
    return p;
  }

  function clear(): void {
    cache.clear();
    inflight.clear();
  }

  function prime(req: TReq, resp: TResp): void {
    store(sig(req), resp);
  }

  return { getCached, fetch, prime, clear };
}
